import { AUDIT_TRIGGERS } from '../../domain/constants/auditTriggers';
import { CHECKOUT_AUDIT_FAILURE_REASONS } from '../../domain/constants/checkoutAuditStatuses';
import { AIUnavailableError } from '../../domain/errors/AIUnavailableError';

/** @typedef {import('../../domain/entities/Audit.types').AuditVerdict} AuditVerdict */
/** @typedef {import('../../domain/constants/checkoutAuditStatuses').CheckoutAuditFailureReason} FailureReason */

/**
 * @typedef {Object} CheckoutAuditOutcome
 * @property {AuditVerdict|null} verdict
 * @property {string|null} auditedNotes    - The exact text reviewed, so a later edit can be spotted.
 * @property {string|null} sessionActivityAuditId
 * @property {string|null} notesHash       - Identifies the note version the result describes.
 * @property {number|null} auditRunCount   - Runs on this note across every client, not just this one.
 * @property {FlagResolution|null} flagResolution - How this run's flags compare with the run it
 *   follows. Null on a first run, and on a leader-side run, which never reports it.
 * @property {FailureReason|null} failureReason
 */

/**
 * @typedef {Object} FlagResolution
 * @property {string[]} resolvedAreas
 * @property {string[]} persistedAreas
 * @property {string[]} newAreas
 * @property {number} resolvedCount
 * @property {number} persistedCount
 * @property {number} newCount
 */

const UNREACHABLE_ERROR_CODES = ['unavailable', 'functions/unavailable'];

/** @type {CheckoutAuditOutcome} */
const NO_OUTCOME = {
  verdict: null,
  auditedNotes: null,
  sessionActivityAuditId: null,
  notesHash: null,
  auditRunCount: null,
  flagResolution: null,
  failureReason: null,
};

function classifyFailure(err) {
  return err instanceof AIUnavailableError || UNREACHABLE_ERROR_CODES.includes(err?.code)
    ? CHECKOUT_AUDIT_FAILURE_REASONS.UNAVAILABLE
    : CHECKOUT_AUDIT_FAILURE_REASONS.ERROR;
} // classifyFailure()

/**
 * Unlike the leader-side run, this one carries the note text: at check-out the note is still in the
 * editor, so the copy on the activity could be outdated.
 *
 * No cancel by design — the audit saves its result server-side even if the dialog is closed.
 */
// eslint-disable-next-line import/prefer-default-export
export function createCheckoutAudit(auditSessionActivity) {
  /** @returns {Promise<CheckoutAuditOutcome>} */
  async function run({ sessionActivity, notes, settings }) {
    try {
      const result = await auditSessionActivity({
        clinicId: sessionActivity.clinic,
        sessionActivityId: sessionActivity.id,
        focusAreas: settings.focusAreas,
        strictness: settings.strictness,
        trigger: AUDIT_TRIGGERS.CHECKOUT,
        notes,
      });

      return {
        ...NO_OUTCOME,
        verdict: result.verdict ?? null,
        auditedNotes: notes,
        sessionActivityAuditId: result.sessionActivityAuditId ?? null,
        notesHash: result.notesHash ?? null,
        auditRunCount: result.auditRunCount ?? null,
        flagResolution: result.flagResolution ?? null,
      };
    } catch (err) {
      return { ...NO_OUTCOME, failureReason: classifyFailure(err) };
    }
  } // run()

  return { run };
}
